import { ThunderboltOutlined } from '@ant-design/icons'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import ActivityIcon from '~/components/Icons/svgs/ActivityIcon'
import AimIcon from '~/components/Icons/svgs/AimIcon'
import ClockSemiboldIcon from '~/components/Icons/svgs/ClockSemiboldIcon'
import PlayOutlineIcon from '~/components/Icons/svgs/PlayOutlineIcon'
import { AvatarPng } from '~/dashboard/types/dashboard.types'
import AvatarWithLevel from '~/statistics-v2/components/AvatarWithLevel'
import BadgeCard from '~/statistics-v2/components/BadgeCard'
import ProfileTag from '~/statistics-v2/components/ProfileTag'
import TrackerCard from '~/statistics-v2/components/TrackerCard'
import { HeaderStats } from '~/statistics-v2/types/statistics.types'
import { formatTimeSpent, getOverallGrade } from '~/statistics-v2/utils/statistics.utils'
import { AppLanguage } from '~/types/locale'

export interface ProfilePanelLeague {
  /** Already translated. */
  name: string
  iconUrl?: string
  /** 1-based. `0` or absent renders the unranked badge. */
  rank?: number
}

export interface ProfilePanelProps {
  /** Renders all five figures as `-` instead of the zeros `headerStats` falls back to. */
  areFiguresUnavailable?: boolean
  avatar: AvatarPng
  gradeTitle?: string
  headerStats: HeaderStats
  /** Gates the five figures. */
  isLoading?: boolean
  /** Gates the league badge, defaulting to `isLoading`. The overall-mark badge stays on `isLoading`. */
  isIdentityLoading?: boolean
  league?: ProfilePanelLeague
  /** Omit when unknown — passing `0` would be the same, but say it. */
  level?: number
  /** The heading. */
  name: string
  /** A `homebase:titles.*` key, not a translated string. */
  userTitle?: string
}

/**
 * A learner's identity and their five headline figures: time spent, XP, video watch time, accuracy
 * and activities.
 *
 * 🔴 Props only. It must not read the store or an endpoint — the caller decides whose data it shows,
 * and the school screen's caller is a teacher's session.
 *
 * Needs the `homebase` and `statisticsV2` namespaces. `league.name` arrives already translated.
 */
export default function ProfilePanel({
  areFiguresUnavailable = false,
  avatar,
  gradeTitle,
  headerStats,
  isIdentityLoading,
  isLoading = false,
  league,
  level,
  name,
  userTitle,
}: ProfilePanelProps): React.ReactElement {
  const { t } = useTranslation(['homebase', 'statisticsV2'])
  const router = useRouter()

  // Derived here so the grade thresholds live in one place.
  // `undefined`, not `null` — that is what `BadgeCard.value` takes.
  const overallMark = areFiguresUnavailable
    ? undefined
    : getOverallGrade(headerStats.accuracy, router.locale as AppLanguage)
  const accuracy = headerStats.showAccuracyWithMustKnows ? headerStats.accuracyWithMustKnows : headerStats.accuracy
  const titleClassName = name.length > 10 ? 'lg:text-4xl' : 'lg:text-7xl'

  /** `TrackerCard` renders `-` for `null`. */
  const figure = <T,>(value: T): T | null => (areFiguresUnavailable ? null : value)

  return (
    <div className="flex w-full max-w-screen-19/20 flex-col items-center gap-4 rounded-9 p-2 md:p-4 lg:gap-10 lg:border lg:border-white/30 lg:bg-gradient-purple-dark-violet">
      <div className="flex w-full items-center justify-center lg:justify-between">
        <div className="flex w-full flex-col items-center lg:flex-row lg:items-center">
          <div className="flex-shrink-0">
            <AvatarWithLevel avatar={avatar} level={level} />
          </div>

          <div className="flex w-full min-w-0 flex-col gap-6 p-4 md:ml-4 md:items-end lg:flex-row">
            <div className="flex min-w-0 flex-1 flex-col items-center gap-4.5 overflow-hidden lg:items-start">
              <h1 className={`text-8 font-extrabold tracking-tighter text-white drop-shadow-lg ${titleClassName}`}>
                {name}
              </h1>

              <div className="flex flex-wrap justify-center gap-2 lg:justify-start">
                {userTitle && <ProfileTag isChecked tag={t(`homebase:${userTitle}`)} />}
                {gradeTitle && <ProfileTag tag={gradeTitle} />}
              </div>
            </div>

            {/* `min-w-54` is 220px in `tailwind.js`. */}
            <div className="flex w-full flex-col items-center gap-2 lg:w-auto lg:min-w-54 lg:items-end lg:gap-4">
              {league && (
                <BadgeCard
                  isLeagueBadge
                  isLoading={isIdentityLoading ?? isLoading}
                  leagueRank={league.rank}
                  srcKey={league.iconUrl || 'icons/products/test_prep_badge.png'}
                  subTitle={league.name}
                  title={t('statisticsV2:currentLeague')}
                  value={`#${league.rank}`}
                />
              )}
              <BadgeCard
                isLoading={isLoading}
                srcKey="icons/products/test_prep_badge.png"
                subTitle={t('statisticsV2:indicated')}
                title={t('statisticsV2:overallMark')}
                value={overallMark}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex w-90 flex-wrap justify-center gap-3 lg:w-full lg:gap-4">
        <TrackerCard
          icon={ClockSemiboldIcon}
          isLoading={isLoading}
          title={t('statisticsV2:timeSpent')}
          value={figure(formatTimeSpent(headerStats.timeSpent))}
        />
        <TrackerCard
          icon={ThunderboltOutlined}
          isLoading={isLoading}
          isXPCard
          title={t('statisticsV2:totalXP')}
          value={figure(headerStats.totalXP)}
        />
        <TrackerCard
          icon={PlayOutlineIcon}
          isLoading={isLoading}
          title={t('statisticsV2:videoWatchTime')}
          value={figure(formatTimeSpent(headerStats.videoWatchTime))}
        />
        <TrackerCard
          icon={AimIcon}
          isLoading={isLoading}
          title={t('statisticsV2:accuracy')}
          value={figure(accuracy > 0 ? `${Math.round(accuracy)}%` : null)}
        />
        <TrackerCard
          icon={ActivityIcon}
          isLoading={isLoading}
          title={t('statisticsV2:activities')}
          value={figure(headerStats.activities)}
        />
      </div>
    </div>
  )
}
